import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useLiveQuery } from "dexie-react-hooks";
import {
  getConfig,
  getCriancaAtiva,
  listarAlimentos,
  registrarEscolhaCrianca,
} from "../db/queries";
import {
  CONFIG_PADRAO,
  GRUPO_LABEL,
  PassoEscada,
  REFEICAO_LABEL,
  type Alimento,
  type Refeicao,
} from "../types";
import { emojiAlimento } from "../lib/emoji";
import { FoodPhoto } from "../ui/FoodPhoto";

/**
 * P0.3 — modo escolha da criança (refeição preparada). Tela cheia, fotos grandes.
 *
 * A criança toca os itens que o cuidador montou em "preparar refeição" e confirma
 * com "pronto" (pode escolher menos que o limite da refeição, configurável). Cada
 * escolha vira uma oferta origem=crianca, pendente de confirmação do cuidador. No
 * fim, um resumo do prato inteiro (marcando a escolha) pra PDF/WhatsApp.
 *
 * O passo registrado é "come um pouco" (engajamento positivo da escolha); o cuidador
 * confirma e ajusta a reação real depois. A sessão é resetada a cada nova entrada.
 */
const MAX_CURADO = 18; // refeição preparada: até 3 por categoria (~6 grupos)
const PASSO_DA_ESCOLHA = PassoEscada.ComeUmPouco;

type EstadoRefeicao = { ids?: string[]; refeicao?: Refeicao } | null;

export function ModoEscolha() {
  const nav = useNavigate();
  const location = useLocation();
  const estado = (location.state as EstadoRefeicao) ?? null;
  const curadoIds = estado?.ids?.length ? estado.ids : null;
  const refeicao = estado?.refeicao ?? null;

  const crianca = useLiveQuery(getCriancaAtiva);
  const cfg = useLiveQuery(getConfig);
  const alimentos = useLiveQuery(() => listarAlimentos()) ?? [];

  const [selecionados, setSelecionados] = useState<string[]>([]); // escolhas da criança
  const [combinado, setCombinado] = useState(false); // trava durante o registro
  // prato final → resumo: `prato` é o prato inteiro montado; `escolhidos` é o que a
  // criança tocou (subconjunto). No PDF/WhatsApp aparece tudo, marcando a escolha.
  const [concluido, setConcluido] = useState<{
    prato: Alimento[];
    escolhidos: string[];
  } | null>(null);

  // a rota /crianca não remonta entre navegações — reseta a sessão a cada entrada
  // nova (location.key muda por navegação) pra não vazar escolhas da refeição anterior
  useEffect(() => {
    setSelecionados([]);
    setCombinado(false);
    setConcluido(null);
  }, [location.key]);

  // os itens que a criança vê: os curados pelo cuidador, na ordem escolhida
  const curados = useMemo(
    () =>
      (curadoIds ?? [])
        .map((id) => alimentos.find((a) => a.id === id))
        .filter((a): a is Alimento => Boolean(a))
        .slice(0, MAX_CURADO),
    [curadoIds, alimentos],
  );

  // quantos itens a criança pode escolher nessa refeição (limitado ao disponível)
  const qtdConfig = refeicao
    ? (cfg?.escolha_qtd_por_refeicao ?? CONFIG_PADRAO.escolha_qtd_por_refeicao)[refeicao]
    : 1;
  const limite = Math.max(1, Math.min(qtdConfig, curados.length || 1));

  function toggle(id: string) {
    if (combinado) return;
    const jaTem = selecionados.includes(id);
    if (!jaTem && selecionados.length >= limite) return; // atingiu o limite
    setSelecionados(jaTem ? selecionados.filter((x) => x !== id) : [...selecionados, id]);
  }

  function limpar() {
    setSelecionados([]);
  }

  async function registrarEscolhas(ids: string[]) {
    if (!crianca || combinado || ids.length === 0) return;
    setCombinado(true); // trava novos toques durante o registro
    for (const id of ids) {
      await registrarEscolhaCrianca(id, crianca.id, PASSO_DA_ESCOLHA, refeicao);
    }
    // prato inteiro = tudo que o cuidador montou; `escolhidos` marca a escolha da criança
    const prato = (curadoIds ?? ids)
      .map((id) => alimentos.find((a) => a.id === id))
      .filter((a): a is Alimento => Boolean(a));
    setConcluido({ prato, escolhidos: ids });
  }

  function gerarPdf() {
    window.print();
  }

  function enviarWhatsApp() {
    if (!concluido) return;
    const dataFmt = new Date().toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
    const texto = textoPedido(
      concluido.prato,
      new Set(concluido.escolhidos),
      refeicao,
      crianca?.apelido ?? null,
      dataFmt,
      crianca?.recomendacoes ?? null,
    );
    const numero = (cfg?.whatsapp_destino ?? "").replace(/\D/g, ""); // só dígitos
    const url = `https://wa.me/${numero}?text=${encodeURIComponent(texto)}`;
    window.open(url, "_blank", "noopener");
  }

  // ── fecho da jornada: resumo do prato inteiro (volta pro cuidador) ──────────
  if (concluido) {
    const dataFmt = new Date().toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
    const escolhidos = new Set(concluido.escolhidos);
    const nPrato = concluido.prato.length;
    const nEscolha = concluido.escolhidos.length;
    return (
      <main className="tela">
        <article className="folha prato-folha">
          <div className="prato-cab">
            <span className="emoji" aria-hidden>
              🍽️
            </span>
            <h1 className="prato-titulo">prato montado</h1>
            <p className="prato-sub">
              {refeicao ? REFEICAO_LABEL[refeicao] : "refeição"} · {nPrato}{" "}
              {nPrato === 1 ? "item" : "itens"}
              {nEscolha < nPrato ? ` · criança escolheu ${nEscolha}` : ""} · {dataFmt}
            </p>
          </div>
          <ul className="prato-lista">
            {concluido.prato.map((a) => {
              const escolhido = escolhidos.has(a.id);
              return (
                <li className={`prato-item${escolhido ? " escolhido" : ""}`} key={a.id}>
                  <span className="prato-emoji" aria-hidden>
                    {emojiAlimento(a.nome, a.categoria)}
                  </span>
                  <span className="prato-nome">{a.nome}</span>
                  {a.grupo && <span className="prato-grupo">{GRUPO_LABEL[a.grupo]}</span>}
                  <span className="prato-marca">
                    {escolhido ? "✓ escolheu" : "planejado"}
                  </span>
                </li>
              );
            })}
          </ul>
          {crianca?.recomendacoes && (
            <p className="prato-recomendacoes">
              📦 <strong>Recomendações:</strong> {crianca.recomendacoes}
            </p>
          )}
          <p className="prato-rodape">
            ✓ = escolhido pela criança · registrado para o cuidador confirmar · Montado
            no Pitada
          </p>
        </article>

        <div className="no-print" style={{ marginTop: "var(--s5)" }}>
          <button
            className="btn btn-bloco btn-grande"
            style={{ background: "#25d366", borderColor: "#25d366", color: "#fff" }}
            onClick={enviarWhatsApp}
          >
            💬 enviar por WhatsApp
          </button>
          <button
            className="btn btn-bloco btn-grande"
            style={{ marginTop: "var(--s2)" }}
            onClick={gerarPdf}
          >
            📄 gerar PDF do prato
          </button>
          <button
            className="btn btn-primario btn-bloco btn-grande"
            style={{ marginTop: "var(--s2)" }}
            onClick={() => nav("/degrau", { replace: true })}
          >
            ✓ concluir
          </button>
        </div>
      </main>
    );
  }

  // celebração breve enquanto registra
  if (combinado) {
    return (
      <div className="crianca">
        <div className="crianca-combinado">
          <span className="emoji" aria-hidden>
            🎉
          </span>
          <span className="txt">combinado!</span>
        </div>
      </div>
    );
  }

  // sem refeição preparada (ex.: abriu /crianca direto): manda preparar primeiro
  if (curados.length === 0) {
    return (
      <div className="crianca">
        <div className="crianca-combinado">
          <span className="emoji" aria-hidden>
            🍽️
          </span>
          <span style={{ color: "var(--text-muted)", fontSize: "1.1rem" }}>
            monte uma refeição primeiro pra criança escolher.
          </span>
          <button className="btn btn-primario btn-grande" onClick={() => nav("/refeicao")}>
            preparar refeição
          </button>
          <button className="btn btn-grande" onClick={() => nav("/degrau")}>
            voltar
          </button>
        </div>
      </div>
    );
  }

  // ── tela da criança ──────────────────────────────────────────────────────
  const prompt = refeicao
    ? `${REFEICAO_LABEL[refeicao]} — o que você quer?`
    : "o que você quer comer?";
  const noLimite = selecionados.length >= limite;

  return (
    <div className="crianca">
      <button
        className="btn-voltar"
        onClick={() => nav("/degrau")}
        aria-label="sair do modo criança"
      >
        ←
      </button>
      <div className="crianca-prompt">{prompt}</div>

      <div className="crianca-progresso" aria-live="polite">
        {Array.from({ length: limite }, (_, i) => (
          <span
            key={i}
            className={`prog-bola${i < selecionados.length ? " cheia" : ""}`}
            aria-hidden
          />
        ))}
        <span className="prog-txt">
          escolheu {selecionados.length} de {limite}
        </span>
      </div>

      {/* grade de cartões grandes */}
      <div className="crianca-opcoes grade">
        {curados.map((a) => {
          const sel = selecionados.includes(a.id);
          const desbotado = !sel && noLimite; // atingiu o limite: não dá pra somar
          return (
            <button
              key={a.id}
              className={`crianca-card${sel ? " escolhido" : ""}${desbotado ? " desbotado" : ""}`}
              aria-pressed={sel}
              onClick={() => toggle(a.id)}
            >
              <FoodPhoto fotoId={a.fotos[0]} nome={a.nome} categoria={a.categoria} />
              <span className="rotulo">
                {sel ? "✓ " : ""}
                {a.nome}
              </span>
            </button>
          );
        })}
      </div>

      <div className="crianca-barra">
        <button
          className="btn crescer btn-grande"
          disabled={selecionados.length === 0}
          onClick={limpar}
        >
          limpar
        </button>
        <button
          className="btn btn-primario crescer btn-grande"
          disabled={selecionados.length === 0}
          onClick={() => registrarEscolhas(selecionados)}
        >
          pronto
        </button>
      </div>
    </div>
  );
}

/** Texto do pedido pro WhatsApp: o prato inteiro, marcando o que a criança escolheu. */
function textoPedido(
  prato: Alimento[],
  escolhidos: Set<string>,
  refeicao: Refeicao | null,
  nomeCrianca: string | null,
  dataFmt: string,
  recomendacoes: string | null,
): string {
  const titulo = refeicao ? REFEICAO_LABEL[refeicao] : "refeição";
  const temPlanejado = prato.some((a) => !escolhidos.has(a.id));
  const linhas = prato.map((a) => `${escolhidos.has(a.id) ? "✅" : "▫️"} ${a.nome}`);
  const partes = [
    `🍽️ *${titulo}*${nomeCrianca ? ` — ${nomeCrianca}` : ""}`,
    dataFmt,
    "",
    ...linhas,
  ];
  // a legenda só faz sentido quando há item planejado que a criança não escolheu
  if (temPlanejado) partes.push("", "✅ escolhido pela criança · ▫️ planejado");
  if (recomendacoes) partes.push("", `📦 *Recomendações:* ${recomendacoes}`);
  partes.push("", "— Montado no Pitada · Degrau");
  return partes.join("\n");
}
